import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";

import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ptBR } from "date-fns/locale";

import CloseIcon from "@mui/icons-material/Close";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import { makeToasterErrorHandler } from "../../common/http/makeToasterErrorHandler";
import { PendingInterface } from "../../common/interfaces";
import { useAuth } from "../../hooks/useAuth";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import {
  SchedulingStatusLabel,
  TypeSchedulingStatus
} from "../enum/schedulingStatusEnum";
import PageToaster from "../toaster/PageToaster";

interface PendingCreateModalProps {
  open: boolean;
  onClose: () => void;
  pending?: PendingInterface | null;
}

const PendingCreateModal: React.FC<PendingCreateModalProps> = ({
  open,
  onClose,
  pending
}) => {
  const { isUser } = useAuth();

  const [description, setDescription] = useState("");
  const [pendingTitle, setPendingTitle] = useState("");

  const [isUpdate, setIsUpdate] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [pendingDate, setPendingDate] = useState<Date | null>(new Date());

  const [statusValue, setStatusValue] = useState<TypeSchedulingStatus>(
    TypeSchedulingStatus.NONE
  );

  const containerId = "toaster-pending-create-modal";
  const { autoSuccess, autoError, autoInfo, autoWarn } = usePageToast(
    containerId,
    true
  );

  const handleHttpError = makeToasterErrorHandler(autoError);

  const clear = () => {
    setIsUpdate(false);
    setPendingDate(null);
    setDescription("");
    setPendingTitle("");
    setStatusValue(TypeSchedulingStatus.NONE);
  };

  // Pré‐preencher campos para edição
  useEffect(() => {
    if (pending) {
      setIsUpdate(true);
      setPendingDate(new Date(pending.pending_date));
      setDescription(pending.pending_description);
      setStatusValue(pending.pending_status);
      setPendingTitle(pending.pending_title);
    } else {
      clear();
    }
  }, [pending]);

  const handleClose = () => {
    clear();
    onClose();
  };

  const handleSubmit = async () => {
    if (!pendingDate) {
      autoError("Selecione data e hora da pendência.");
      return;
    }

    if (pendingTitle.length === 0) {
      autoError("Título da pendência é obrigatório.");
      return;
    }

    if (statusValue === TypeSchedulingStatus.NONE) {
      autoError("Selecione um status válido.");
      return;
    }

    setIsSubmitting(true);
    const payload = {
      pending_date: pendingDate.toISOString(),
      pending_description: description,
      pending_title: pendingTitle,
      pending_status: statusValue
    };

    try {
      if (isUpdate && pending) {
        await api.put(`/pending/${pending.pending_id}`, payload);
        autoSuccess("Pendência atualizada com sucesso!");
      } else {
        await api.post("/pending", payload);
        autoSuccess("Pendência criada com sucesso!");
      }

      clear();
      onClose();
    } catch (err: any) {
      console.error("Erro ao salvar pendência:", err.response?.data || err);
      handleHttpError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <PageToaster containerId={containerId} />
      <Modal open={open} onClose={handleClose} aria-labelledby="modal-scheduling">
        <Box
          sx={{
            position: "absolute" as const,
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            border: "1px solid var(--border-strong)",
            p: 4,
            borderRadius: 2,
            boxShadow: 24,
            width: { xs: "90vw", sm: "500px" },
            maxHeight: "90vh",
            overflowY: "auto"
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">
              {isUpdate ? "Editar Pendencia" : "Novo Pendencia"}
            </Typography>
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={ptBR}
          >
            <DateTimePicker
              label="Data e Hora do Pendencia"
              value={pendingDate}
              onChange={setPendingDate}
              format="dd/MM/yyyy HH:mm"
              viewRenderers={{
                hours: renderTimeViewClock,
                minutes: renderTimeViewClock,
                seconds: renderTimeViewClock
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: "normal"
                }
              }}
            />
          </LocalizationProvider>

          <TextField
            fullWidth
            label="Titulo da Pendência"
            value={pendingTitle}
            onChange={(e) => setPendingTitle(e.target.value)}
            margin="normal"
            multiline
            minRows={2}
          />

          <TextField
            fullWidth
            label="Descrição"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            margin="normal"
            multiline
            minRows={2}
          />

          <FormControl fullWidth margin="normal">
            <InputLabel id="label-status">Status</InputLabel>
            <Select
              labelId="label-status"
              value={statusValue}
              label="Status"
              onChange={(e) =>
                setStatusValue(e.target.value as TypeSchedulingStatus)
              }
            >
              {(Object.values(TypeSchedulingStatus) as TypeSchedulingStatus[])
                .filter(
                  (code) => code !== TypeSchedulingStatus.NONE // remove o “”
                )
                .map((statusCode) => (
                  <MenuItem key={statusCode} value={statusCode}>
                    {SchedulingStatusLabel[statusCode]}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {/* Botões de ação */}
          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Button onClick={handleClose} color="secondary" sx={{ mr: 2 }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              // disabled={isSubmitting || isUser}
            >
              {isUpdate ? "Atualizar" : "Cadastrar"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default PendingCreateModal;
